import React, { createContext, useContext, useState } from 'react';
import { useBooleanState } from 'react-use-object-state';
import { Entity, Vertex } from '../../../../../types';
import {
  DescribedEntityHook,
  useDescribedEntityHook,
} from '../useDescribedEntity';

export enum PromptMode {
  Closed = 'closed',
  Edit = 'edit',
  Delete = 'delete',
}

export interface EntityDrawerState {
  promptMode: PromptMode;
  promptMessage?: string;
}

export interface PropertyActions {
  name: string;
  callback: (name: string, value: any, classType?: string) => void;
}

interface SharedProps {
  onClose?: VoidFunction;
  propertyFindRelated?: boolean;
  propertyFindSimilar?: boolean;
  propertyActions?: PropertyActions[];
  isDocked?: boolean;
  noDataEdit?: boolean;
}
export interface EntityDrawerPropsStatic extends SharedProps {
  vertex?: Vertex;
}

export interface EntityDrawerPropsApollo extends SharedProps {
  vertexId?: string;
  onEditEntity?: (entity: Entity) => void;
  onDeleteEntity?: (entityID: string) => void;
}

type Props = EntityDrawerPropsApollo & EntityDrawerPropsStatic;

interface ModalState {
  state: boolean;
  open: (prompt?: boolean) => void | VoidFunction;
  close: VoidFunction;
}
interface EntityDrawerHook {
  isOpen: boolean;
  entity: DescribedEntityHook;
  promptMode: PromptMode;
  setPromptMode: (promptMode: PromptMode) => void;
  onClose?: VoidFunction;
  onEditEntity?: (entity: Entity) => void;
  onDeleteEntity?: (entityID: string) => void;
  editModal?: ModalState;
  deleteModal?: ModalState;
  rawDataModal: ModalState;
  propertyFindRelated: boolean;
  propertyFindSimilar: boolean;
  propertyActions?: PropertyActions[];
  isDocked: boolean;
}

export const useEntityDrawerHook = ({
  vertexId,
  vertex,
  onClose,
  onEditEntity,
  onDeleteEntity,
  propertyFindRelated = false,
  propertyFindSimilar = false,
  propertyActions,
  isDocked = false,
  noDataEdit = false,
}: Props): EntityDrawerHook => {
  const handleDeleteEntity: EntityDrawerPropsApollo['onDeleteEntity'] = (
    entity
  ) => {
    onClose?.();
    onDeleteEntity?.(entity);
  };
  const entity = useDescribedEntityHook({
    vertex,
    vertexId,
    onEditEntity,
    onDeleteEntity: handleDeleteEntity,
  });
  const editModal = useBooleanState(false);
  const deleteModal = useBooleanState(false);
  const rawDataModal = useBooleanState(false);
  const [promptMode, setPromptMode] = useState<PromptMode>(PromptMode.Closed);

  const editModalOpen: ModalState['open'] = (prompt) => {
    if (prompt) {
      setPromptMode(PromptMode.Edit);
    } else editModal.setTrue();
  };

  const deleteModalOpen: ModalState['open'] = (prompt) => {
    if (prompt) {
      setPromptMode(PromptMode.Delete);
    } else deleteModal.setTrue();
  };

  return {
    promptMode,
    setPromptMode,
    entity,
    isOpen: !!(vertex || vertexId),
    onClose,
    editModal:
      !noDataEdit && (vertexId || vertex?.id)
        ? {
            state: editModal.state,
            open: editModalOpen,
            close: editModal.setFalse,
          }
        : undefined,
    deleteModal:
      !noDataEdit && (vertexId || vertex?.id)
        ? {
            state: deleteModal.state,
            open: deleteModalOpen,
            close: deleteModal.setFalse,
          }
        : undefined,
    rawDataModal: {
      state: rawDataModal.state,
      open: rawDataModal.setTrue,
      close: rawDataModal.setFalse,
    },
    propertyFindRelated,
    propertyFindSimilar,
    propertyActions,
    isDocked,
  };
};

interface EntityDrawerProviderProps extends Props {
  children: React.ReactNode;
}
const EntityDrawerContext = createContext<EntityDrawerHook>(
  {} as EntityDrawerHook
);

export const EntityDrawerProvider = ({
  children,
  ...rest
}: EntityDrawerProviderProps) => {
  const contextValue = useEntityDrawerHook(rest);
  return (
    <EntityDrawerContext.Provider value={contextValue}>
      {children}
    </EntityDrawerContext.Provider>
  );
};

const useEntityDrawerContext = () => {
  return useContext(EntityDrawerContext);
};
export default useEntityDrawerContext;
